import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import recoveryIllustration from "@/assets/froppio-password-reset.svg";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { authService, passwordUpdatedNoticeKey } from "@/core/auth/services/authService";
import { trackEvent } from "@/core/telemetry/telemetry";
import { Button } from "@/shared/components/shadcn/Button";
import { Input } from "@/shared/components/shadcn/Input";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { FieldGroup } from "@/shared/components/ui/FieldGroup";
import { FormField } from "@/shared/components/ui/FormField";
import { FormPage } from "@/shared/components/ui/FormPage";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent, PageFooter } from "@/shared/components/ui/Page";
import { EntryHeader } from "../components/EntryHeader";
import { userAction } from "@/core/telemetry/userAction";

function hasLinkError() {
  const hash = new URLSearchParams(window.location.hash.slice(1));
  const query = new URLSearchParams(window.location.search);
  return hash.has("error") || hash.has("error_code") || query.has("error") || query.has("error_code");
}

function ResetStatus({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <Page>
      <PageContent className="[scrollbar-gutter:auto]">
        <div className="mx-auto flex min-h-full w-full max-w-md flex-col justify-center py-6">
          <EntryHeader title={title} illustration={recoveryIllustration} illustrationAlt="Froppio holding a password reset envelope" />
          <div aria-live="polite" className="space-y-3 text-center text-sm leading-relaxed text-muted-foreground">{children}</div>
        </div>
      </PageContent>
      {action && <PageFooter narrow>{action}</PageFooter>}
    </Page>
  );
}

export function PasswordResetPage() {
  const { session, loading: authLoading, signOut } = useAuth();
  const [linkError] = useState(hasLinkError);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [updated, setUpdated] = useState(false);
  const submitting = useRef(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting.current) return;
    if (password !== confirmation) {
      setError("Passwords don't match.");
      return;
    }
    submitting.current = true;
    setError("");
    setSaving(true);
    try {
      await authService.updatePassword(password);
      trackEvent("auth.password_updated", { provider: authService.kind });
      setPassword("");
      setConfirmation("");
      setUpdated(true);
      sessionStorage.setItem(passwordUpdatedNoticeKey, "1");
      // The password is already changed; a failed sign-out must not hide that success.
      await signOut().catch(() => sessionStorage.removeItem(passwordUpdatedNoticeKey));
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't update your password. Please try again.");
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  if (updated) {
    return (
      <ResetStatus
        title="Password updated"
        action={<Button asChild size="touch"><Link to="/login" replace>Sign in</Link></Button>}
      >
        <p>Your password has been changed. Sign in with your new password.</p>
        <p>If you started the reset in the Froppio app, return there to sign in.</p>
      </ResetStatus>
    );
  }

  if (authLoading && !linkError) {
    return (
      <ResetStatus title="Checking your reset link">
        <LoadingSpinner message="Checking your reset link..." />
      </ResetStatus>
    );
  }

  if (linkError || !session) {
    return (
      <ResetStatus
        title="Unable to reset password"
        action={<Button asChild size="touch"><Link to="/forgot-password" replace>Request a new link</Link></Button>}
      >
        <ErrorState message="This reset link may have expired or already been used. Request a new link to choose a new password." />
      </ResetStatus>
    );
  }

  return (
    <FormPage
      header={null}
      narrow
      contentClassName="flex min-h-full flex-col justify-center space-y-0 py-6"
      onSubmit={handleSubmit}
      actions={
        <Button type="submit" {...userAction("auth.password_reset_submit")} size="touch" disabled={saving}>
          {saving ? "Saving..." : "Save new password"}
        </Button>
      }
    >
      <EntryHeader
        title="Choose a new password"
        illustration={recoveryIllustration}
        illustrationAlt="Froppio holding a password reset envelope"
      />
      <div className="space-y-6">
        {session.user.email && (
          <p className="text-sm leading-relaxed text-muted-foreground">
            Set a new password for <strong className="break-all text-foreground" data-telemetry-mask>{session.user.email}</strong>.
          </p>
        )}
        {error && <ErrorState message={error} />}
        <FieldGroup disabled={saving}>
          <FormField id="new-password" label="New password">
            <Input name="new-password" type="password" autoComplete="new-password" required value={password} onChange={(event) => setPassword(event.target.value)} />
          </FormField>
          <FormField id="confirm-password" label="Confirm new password">
            <Input name="confirm-password" type="password" autoComplete="new-password" required value={confirmation} onChange={(event) => setConfirmation(event.target.value)} />
          </FormField>
        </FieldGroup>
      </div>
    </FormPage>
  );
}
